import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import { Coordinate, Location, TileSize } from "../../models"
import { RoofInteriorRect, withinRoofInterior } from "./roof_interior"

/** Logical coordinates are shared by parent and child; elevation is presentation only. */
export interface TwoStoreyHouse {
    buildingId: string
    left: number
    top: number
    width: number
    depth: number
    rise: number
    floor: number
    groundRoomId: string
    upperRoomId: string
    floorRoomIds?: string[]
    stairs?: { coordinate: Coordinate; landing: Location; label: "UP" | "DOWN" }[]
    entranceX: number
    /** Explicit cell masks support irregular footprints and different shapes on each storey. */
    floorCells: Coordinate[]
    /** Roof coverage is independent of the inset floor artwork and recessed upper storey. */
    roofCells?: Coordinate[]
    interiorCells: Coordinate[]
    /** Live wall boundaries in feet coordinates; omitted for open shelters. */
    interiorRects?: RoofInteriorRect[]
    wallCells: Coordinate[]
    stair: Coordinate
    landing: Location
}

export const cellKey = (at: Coordinate): string => `${at.x},${at.y}`
export const roofCellsForHouse = (house: TwoStoreyHouse): Coordinate[] => {
    if (house.roofCells) return house.roofCells
    // Earlier prototype camps only recorded the floor footprint. Include a southern eave
    // so the raised roof's bottom tiles reach the top of the front wall.
    const cells = new Map(house.floorCells.map(cell => [cellKey(cell), cell]))
    house.floorCells.forEach(({ x, y }) => cells.set(`${x},${y + 1}`, { x, y: y + 1 }))
    return [...cells.values()]
}
export const insideHouse = (house: TwoStoreyHouse, at: Coordinate): boolean => {
    const x = Math.floor(at.x / TileSize)
    const feetY = Math.floor((at.y + BODY_GROUND_OFFSET) / TileSize)
    // The ground anchor needs a covered tile and the interior side of its walls.
    // Derived per-tile bounds already include the roof-coverage requirement.
    if (house.interiorRects) return withinRoofInterior(house.interiorRects, { x: at.x, y: at.y + BODY_GROUND_OFFSET })
    return house.interiorCells.some(cell => cell.x === x && cell.y === feetY)
}
export const houseStairs = (house: TwoStoreyHouse) => house.stairs ?? (house.upperRoomId
    ? [{ coordinate: house.stair, landing: house.landing, label: house.floor === 0 ? "UP" as const : "DOWN" as const }] : [])
export const hasHouseRoof = (house?: TwoStoreyHouse): boolean => !!house &&
    (house.roofCells !== undefined ? house.roofCells.length > 0 : house.floor === 0 || !!house.floorRoomIds?.[house.floor + 1])
export const onHouseStair = (house: TwoStoreyHouse, at: Coordinate): boolean =>
    Math.floor(at.x / TileSize) === house.stair.x && Math.floor(at.y / TileSize) === house.stair.y
export const houseWallAt = (house: TwoStoreyHouse, x: number, y: number): boolean =>
    house.wallCells.some(cell => cell.x === x && cell.y === y)

/** A frame's scene visibility never determines its simulation or collision membership. */
export const companionFloorVisible = (active: TwoStoreyHouse, at: Coordinate): boolean =>
    active.floor > 0 || !insideHouse(active, at)
/** Presentation height of each storey; independent of the 32px logical tile grid. */
export let STOREY_RISE_PX = 48
/** Client-side live tuning. Each runtime retains its own value until reload. */
export const setStoreyRisePx = (rise: number): void => {
    if (Number.isFinite(rise)) STOREY_RISE_PX = Math.round(rise)
}
/** Roof uplift from its placement cell, independent of storey spacing. */
export let ROOF_UPLIFT_PX = 36
export const setRoofUpliftPx = (uplift: number): void => {
    if (Number.isFinite(uplift)) ROOF_UPLIFT_PX = Math.round(uplift)
}
// Floors are zero-based: level 2 rises 48px, level 3 rises 96px, and so on.
export const floorElevation = (house: TwoStoreyHouse): number =>
    Math.max(0, house.floor) * house.rise * STOREY_RISE_PX
export const companionFloorOffset = (active: TwoStoreyHouse, companion: TwoStoreyHouse): number =>
    floorElevation(active) - floorElevation(companion)
/** Roof artwork lifts independently of the offset between subworld levels. */
export const houseRoofOffset = (_house?: TwoStoreyHouse, _viewFloor?: number): number => -ROOF_UPLIFT_PX
